import React, { ChangeEvent, useCallback, useEffect, useState } from "react";
import styled from "@emotion/styled";
import { ApiService } from "src/common";
import { InputLabel } from "src/components";
import { colors } from "src/theme";

const Box = styled.div({
  display: 'flex',
  flexDirection: 'column',
  gap: '8px'
})

const Input = styled.input((props: { hasError: boolean }) => ({
  boxSizing: 'border-box' as const,
  outline: 'none',
  display: 'block',
  width: '100%',
  height: '44px',
  padding: '9px 12px',
  fontSize: '18px',
  fontWeight: 400,
  fontFamily: "Red Hat Display",
  fontStyle: 'normal',
  lineHeight: '24px',
  color: colors.text1,
  backgroundColor: 'transparent',
  borderRadius: '8px',
  border: `1px solid ${props.hasError ? colors.error1 : colors.border5}`,
  transition: 'border-color .15s ease-in-out, box-shadow .15s ease-in-out',

  '&[disabled]': {
    border: `1px solid ${colors.border6}`,
    color: colors.text4,
    pointerEvents: 'none' as const,
  },

  '&::-moz-placeholder': {
    color: colors.text4,
    opacity: 1,
  },

  '&:-ms-input-placeholder': {
    color: colors.text4,
  },

  '&::-webkit-input-placeholder': {
    color: colors.text4,
  },

  '&:focus::-moz-placeholder': {
    color: 'transparent',
    opacity: 1,
  },

  '&:focus:-ms-input-placeholder': {
    color: 'transparent',
  },

  '&:focus::-webkit-input-placeholder': {
    color: 'transparent',
  },

  '&:focus': {
    borderColor: colors.border4,
  },

  '&[type=number]::-webkit-inner-spin-button, &[type=number]::-webkit-outer-spin-button': {
    WebkitAppearance: 'none' as const,
    MozAppearance: 'none' as const,
    appearance: 'none' as const,
  },
}));

const ValidationError = styled.div({
  position: 'absolute',
  color: colors.error1,
  fontSize: '14px',
  fontWeight: 400,
  lineHeight: '1.14',
  marginBottom: 0,
  paddingTop: '4px',
  transition: 'all 0.4s ease',
});

const CityList = styled.div({
  width: '100%',
  boxSizing: 'border-box' as const,
  outline: 'none',
  display: 'block',
  padding: '9px 12px',
  fontSize: '18px',
  fontWeight: 400,
  fontStyle: 'normal',
  lineHeight: '24px',
  color: colors.text1,
  borderRadius: '8px',
  border: `1px solid ${colors.border5}`,
  transition: 'border-color .15s ease-in-out, box-shadow .15s ease-in-out',
  backgroundColor: '#fff',
  maxHeight: '150px',
  overflowY: 'auto' as const,
  zIndex: 999,
});

const CityItem = styled.div({
  cursor: 'pointer',

  '&:hover': {
    backgroundColor: colors.background6,
  }
})

export type InputFieldPropsCity = {
  id?: string;
  label?: string;
  value?: string | number;
  error?: string;
  disabled?: boolean;
  onChange?: (value: { city: string, cityKompareId: string, zipCode: number, street: string, streetKompareId: string }, isValid: boolean) => void;
};

interface CityData {
  readonly cityKompareId: string;
  readonly cityName: string;
  readonly cityParentName: string;
  readonly postalCode: number;
}

export const InputCity: React.FC<InputFieldPropsCity> = ({
  id,
  label,
  value,
  error,
  disabled,
  onChange
}) => {
  const [inputValue, setInputValue] = useState(value || '');
  const [showCityList, setShowCityList] = useState(false);
  const [citySelected, setCitySelected] = useState(false);
  const [inputTimeout, setInputTimeout] = useState<NodeJS.Timeout | null>(null);
  const [cityData, setCityData] = useState<CityData[]>([]);

  useEffect(() => {
    setInputValue(value || '');
  }, [value]);

  const fetchCities = (query: string) => {
    ApiService.getCities(query).subscribe({
      next: (cities: CityData[]) => {
        setCityData(cities);
      },
      error: () => {
        console.log('Error while getting cities')
      },
    });
  };

  const fetchCitiesDebounced = useCallback((query: string) => {
    if (inputTimeout) {
      clearTimeout(inputTimeout);
    }

    const timeout = setTimeout(() => {
      fetchCities(query);
    }, 500);

    setInputTimeout(timeout);
  }, [inputTimeout]);

  const resetCitySelected = () => {
    setCitySelected(false);
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    const newValue = e.target.value;

    setInputValue(newValue);

    fetchCitiesDebounced(newValue);

    setShowCityList(true);
    resetCitySelected();
  };

  const handleCityItemClick = (city: CityData) => {
    setInputValue(`${city.cityName}, ${city.postalCode}`);
    setCitySelected(true);
    setShowCityList(false);
    if (onChange) {
      onChange({ cityKompareId: city.cityKompareId, city: city.cityName, zipCode: city.postalCode, street: '', streetKompareId: '' }, !!city.cityKompareId);
    }
  };

  // Cleanup timeout on unmount
  useEffect(() => () => {
    if (inputTimeout) {
      clearTimeout(inputTimeout);
    }
  }, [inputTimeout]);

  return (
    <Box>
      <div>
        {label ? <InputLabel htmlFor={id} disabled={disabled}>{label}</InputLabel> : null}
        <Input
          autoComplete="new-city-lvl2"
          id={id}
          type='text'
          value={inputValue}
          disabled={disabled}
          onChange={handleChange}
          hasError={!!error}
        />
        {error ? <ValidationError>{error}</ValidationError> : null}
      </div>
      {showCityList && cityData && cityData.length > 0 && !citySelected && (
        <CityList>
          {cityData.length === 0 || cityData.every(city => !city.cityName) ? (
            <CityItem>Nema rezultata</CityItem>
          ) : (
            cityData.map(city => (
              <CityItem key={city.cityKompareId} onClick={() => handleCityItemClick(city)}>
                {`${city.cityName}, ${city.postalCode}`}
              </CityItem>
            ))
          )}
        </CityList>
      )}
    </Box>
  );
};
